import esriId from '@arcgis/core/identity/IdentityManager';

// Flag to track if error handlers have been set up
let errorHandlersInitialized = false;

/**
 * Sets up global error handlers for ESRI IdentityManager errors.
 * These errors occur due to a bug in @arcgis/core 4.23.0 and can be safely ignored.
 */
function setupErrorHandlers() {
  if (errorHandlersInitialized) return;
  errorHandlersInitialized = true;

  // Catch unhandled promise rejections from ESRI's IdentityManager
  // The "Cannot access 'h' before initialization" error is a known bug in older ESRI SDK versions
  window.addEventListener('unhandledrejection', event => {
    const error = event.reason;
    const errorMessage = error?.message || String(error);
    const errorStack = error?.stack || '';

    // Check if this is an ESRI IdentityManager error
    const isEsriIdentityError =
      errorStack.includes('IdentityManager') ||
      errorStack.includes('signIn') ||
      errorMessage.includes('Cannot access') ||
      errorMessage.includes('before initialization');

    if (isEsriIdentityError) {
      // Suppress the error - it's a known ESRI SDK bug that doesn't affect functionality
      event.preventDefault();
    }
  });
}

/**
 * Registers the ArcGIS server token with the IdentityManager so that GIS services can be used.
 * https://developers.arcgis.com/javascript/latest/api-reference/esri-identity-IdentityManager.html#registerToken
 * @param {String} server - ArcGIS server url
 * @param {String} token - ArcGIS server token
 */
export const registerToken = async (server, token) =>
  await handleToken(server, token);

function handleToken(server = '', token = '') {
  // Set up error handlers on first token registration
  setupErrorHandlers();

  const tokenProperties = {
    server,
    token,
  };

  esriId.registerToken(tokenProperties);

  // There is a sproradic issue that occurs where the IdentityManager dialog widget is rendered over
  // the Esri map, prompting the user to sign-in in order to access the resource. The cause could be
  // that a GIS service layer has changed or is unavailable. The snippet below checks if the dialog
  // widget is rendered, destroys the dialog widget instance, then attempts to register the token again.
  // https://community.esri.com/t5/arcgis-javascript-maps-sdk-questions/detect-or-prevent-the-arcgis-authentication-dialog/td-p/1047565
  // https://community.esri.com/t5/arcgis-javascript-maps-sdk-questions/disabling-the-esri-javascript-api-v4-x/td-p/1223257
  // https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Widget.html#visible
  // https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Widget.html#destroy
  esriId.on('dialog-create', () => {
    if (esriId?.dialog?.open || esriId?.dialog?.visible) {
      esriId.dialog.destroy();
      esriId.registerToken(tokenProperties);
    }
  });
}
